/**
 * GST Filing Preview Panel — iter-252e (PR 7)
 * ============================================
 * Read-only UI on top of the deployed
 *   GET /api/admin/gst-filing/preview
 * endpoint (D-034 — orders are the financial SSOT).
 *
 * Renders:
 *   • Period selector: Current Month / Previous Month / Custom Date Range
 *   • Headline KPIs: Taxable Turnover, CGST, SGST, IGST, Total GST,
 *                    Order Count, Refunded, Net Sales
 *   • Vendor-wise breakup table
 *   • Export CSV (client-side, from include_orders=1 payload)
 *
 * No new business logic. No projection writes. Just consumption.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { toast } from 'sonner';
import {
  Calendar, Download, RefreshCw, FileText, AlertTriangle,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const PERIOD_LABELS = {
  current_month:  'Current Month (MTD)',
  previous_month: 'Previous Month',
  custom:         'Custom Range',
};

const formatINR = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ─── CSV utility ────────────────────────────────────────────────────
const csvEscape = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const downloadCsv = (filename, rows) => {
  if (!rows || rows.length === 0) {
    toast.error('Nothing to export — period has zero orders.');
    return;
  }
  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(','),
    ...rows.map((r) => headers.map((h) => csvEscape(r[h])).join(',')),
  ].join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// ─── Stat Card ──────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, tone = 'default', testid }) => {
  const toneCls = {
    default:  'bg-white',
    primary:  'bg-blue-50 border-blue-200',
    success:  'bg-emerald-50 border-emerald-200',
    warning:  'bg-amber-50 border-amber-200',
    danger:   'bg-rose-50 border-rose-200',
  }[tone] || 'bg-white';
  return (
    <Card className={`border ${toneCls}`} data-testid={testid}>
      <CardContent className="p-4">
        <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</div>
        <div className="text-2xl font-bold mt-1 font-mono">{value}</div>
        {sub ? <div className="text-xs text-slate-400 mt-1">{sub}</div> : null}
      </CardContent>
    </Card>
  );
};

// ─── Period Selector ────────────────────────────────────────────────
const PeriodSelector = ({ period, customFrom, customTo, onPeriodChange, onCustomChange }) => {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="gst-filing-period-selector">
      {(['current_month', 'previous_month', 'custom']).map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onPeriodChange(p)}
          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
            period === p
              ? 'bg-blue-600 text-white shadow-sm'
              : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
          }`}
          data-testid={`gst-filing-period-${p}`}
        >
          {PERIOD_LABELS[p]}
        </button>
      ))}
      {period === 'custom' && (
        <div className="flex items-center gap-2 ml-2" data-testid="gst-filing-custom-range">
          <Calendar className="w-4 h-4 text-slate-400" />
          <input
            type="date"
            value={customFrom}
            onChange={(e) => onCustomChange({ from: e.target.value, to: customTo })}
            className="px-2 py-1 border border-slate-200 rounded text-sm"
            data-testid="gst-filing-custom-from"
          />
          <span className="text-slate-400">→</span>
          <input
            type="date"
            value={customTo}
            onChange={(e) => onCustomChange({ from: customFrom, to: e.target.value })}
            className="px-2 py-1 border border-slate-200 rounded text-sm"
            data-testid="gst-filing-custom-to"
          />
        </div>
      )}
    </div>
  );
};

// ─── Main Panel ─────────────────────────────────────────────────────
export default function GstFilingPreviewPanel() {
  const { apiCall } = useAuth();
  const [period, setPeriod] = useState('previous_month');
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = new Date();
  firstOfMonth.setDate(1);
  const [customFrom, setCustomFrom] = useState(firstOfMonth.toISOString().slice(0, 10));
  const [customTo, setCustomTo] = useState(today);

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // ───── Period payload (memoised so handlers stay stable) ────────
  const queryString = useMemo(() => {
    if (period === 'custom') {
      return `?period=custom&from=${customFrom}&to=${customTo}`;
    }
    return `?period=${period}`;
  }, [period, customFrom, customTo]);

  // ───── Fetch ────────────────────────────────────────────────────
  const fetchReport = useCallback(async () => {
    // Skip if custom range is invalid.
    if (period === 'custom' && (!customFrom || !customTo)) return;
    if (period === 'custom' && customFrom > customTo) {
      toast.error('From-date must be earlier than To-date.');
      return;
    }
    setLoading(true);
    try {
      const data = await apiCall(`/api/admin/gst-filing/preview${queryString}`);
      setReport(data);
    } catch (e) {
      toast.error(`Failed to load GST preview: ${e.message || e}`);
    } finally {
      setLoading(false);
    }
  }, [apiCall, queryString, period, customFrom, customTo]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  // ───── CSV exporters ────────────────────────────────────────────
  const exportSummaryCsv = useCallback(() => {
    if (!report) return;
    const t = report.totals || {};
    const rows = [
      { Metric: 'Period', Value: report.period?.label || '' },
      { Metric: 'Order Count', Value: t.order_count },
      { Metric: 'Taxable Order Count', Value: t.taxable_order_count },
      { Metric: 'Gross Sales (INR)', Value: t.gross_inr },
      { Metric: 'Taxable Turnover / Subtotal (INR)', Value: t.subtotal_inr },
      { Metric: 'Refunded Sales (INR)', Value: t.refunded_inr },
      { Metric: 'Net Sales (INR)', Value: t.net_inr },
      { Metric: 'CGST Collected (INR)', Value: t.cgst_inr },
      { Metric: 'SGST Collected (INR)', Value: t.sgst_inr },
      { Metric: 'IGST Collected (INR)', Value: t.igst_inr },
      { Metric: 'Total GST (INR)', Value: t.gst_total_inr },
    ];
    const label = (report.period?.label || period).replace(/[^a-z0-9_-]/gi, '_');
    downloadCsv(`gst_summary_${label}.csv`, rows);
  }, [report, period]);

  const exportVendorBreakupCsv = useCallback(() => {
    if (!report) return;
    const rows = (report.vendor_breakup || []).map((v) => ({
      'Vendor ID':           v.vendor_id,
      'Vendor Name':         v.vendor_name,
      'Order Count':         v.order_count,
      'Taxable Orders':      v.taxable_order_count,
      'Gross (INR)':         v.gross_inr,
      'Subtotal (INR)':      v.subtotal_inr,
      'CGST (INR)':          v.cgst_inr,
      'SGST (INR)':          v.sgst_inr,
      'IGST (INR)':          v.igst_inr,
      'Total GST (INR)':     v.gst_total_inr,
    }));
    const label = (report.period?.label || period).replace(/[^a-z0-9_-]/gi, '_');
    downloadCsv(`gst_vendor_breakup_${label}.csv`, rows);
  }, [report, period]);

  const exportOrderwiseCsv = useCallback(async () => {
    setExporting(true);
    try {
      const data = await apiCall(`/api/admin/gst-filing/preview${queryString}&include_orders=1`);
      const orders = data?.orders || [];
      if (orders.length === 0) {
        toast.error('No orders in the selected period.');
        return;
      }
      const label = (data.period?.label || period).replace(/[^a-z0-9_-]/gi, '_');
      downloadCsv(`gst_orders_${label}.csv`, orders);
      toast.success(`Exported ${orders.length} orders`);
    } catch (e) {
      toast.error(`Export failed: ${e.message || e}`);
    } finally {
      setExporting(false);
    }
  }, [apiCall, queryString, period]);

  // ───── Render ────────────────────────────────────────────────────
  const totals = report?.totals || {};
  const vendorBreakup = report?.vendor_breakup || [];
  const warnings = report?.warnings || [];

  return (
    <div className="space-y-6" data-testid="gst-filing-preview-panel">
      {/* Header card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                GST Filing Preview
              </CardTitle>
              <CardDescription>
                Read-only filing-ready CGST/SGST/IGST totals + vendor breakup. Computed live from <code className="text-xs">db.orders</code> + <code className="text-xs">db.refunds</code> (D-034 SSOT). Does not depend on <code className="text-xs">tax_ledger</code>.
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchReport}
              disabled={loading}
              data-testid="gst-filing-refresh"
              className="gap-1"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <PeriodSelector
            period={period}
            customFrom={customFrom}
            customTo={customTo}
            onPeriodChange={setPeriod}
            onCustomChange={({ from, to }) => { setCustomFrom(from); setCustomTo(to); }}
          />
          {report?.period && (
            <div className="text-xs text-slate-500 mt-3 flex items-center gap-2" data-testid="gst-filing-period-label">
              <span>Showing:</span>
              <Badge variant="outline" className="font-mono">{report.period.label}</Badge>
              <span className="text-slate-400">·</span>
              <span className="font-mono">{report.period.from} → {report.period.to}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Loading state */}
      {loading && !report && (
        <div className="text-sm text-slate-500 py-8 text-center" data-testid="gst-filing-loading">
          Loading GST preview…
        </div>
      )}

      {/* Warnings */}
      {warnings.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/40" data-testid="gst-filing-warnings">
          <CardContent className="p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-sm text-amber-900 space-y-1">
                {warnings.map((w, i) => (
                  <div key={i} data-testid={`gst-filing-warning-${i}`}>• {w}</div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Headline KPIs */}
      {report && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard
              label="Order Count"
              value={Number(totals.order_count || 0).toLocaleString('en-IN')}
              sub={`${totals.taxable_order_count || 0} taxable`}
              testid="gst-stat-order-count"
            />
            <StatCard
              label="Taxable Turnover"
              value={formatINR(totals.subtotal_inr)}
              sub="Pre-tax subtotal"
              tone="primary"
              testid="gst-stat-taxable-turnover"
            />
            <StatCard
              label="Total GST"
              value={formatINR(totals.gst_total_inr)}
              sub="CGST + SGST + IGST"
              tone="success"
              testid="gst-stat-total-gst"
            />
            <StatCard
              label="Net Sales"
              value={formatINR(totals.net_inr)}
              sub={`Gross ${formatINR(totals.gross_inr)} − Refunded ${formatINR(totals.refunded_inr)}`}
              testid="gst-stat-net-sales"
            />
          </div>

          {/* GST split */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <StatCard
              label="CGST Collected"
              value={formatINR(totals.cgst_inr)}
              sub="Intra-state · Central component"
              testid="gst-stat-cgst"
            />
            <StatCard
              label="SGST / UTGST Collected"
              value={formatINR(totals.sgst_inr)}
              sub="Intra-state · State / Union Territory"
              testid="gst-stat-sgst"
            />
            <StatCard
              label="IGST Collected"
              value={formatINR(totals.igst_inr)}
              sub="Inter-state · Single component"
              testid="gst-stat-igst"
            />
          </div>

          {/* Export buttons */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Export</CardTitle>
              <CardDescription className="text-xs">
                CSV downloads include UTF-8 BOM for Excel compatibility. Hand to your CA directly.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={exportSummaryCsv}
                  disabled={!report}
                  data-testid="gst-export-summary"
                  className="gap-2"
                >
                  <Download className="w-4 h-4" />
                  Summary CSV
                </Button>
                <Button
                  variant="outline"
                  onClick={exportVendorBreakupCsv}
                  disabled={!report || vendorBreakup.length === 0}
                  data-testid="gst-export-vendor-breakup"
                  className="gap-2"
                >
                  <Download className="w-4 h-4" />
                  Vendor Breakup CSV ({vendorBreakup.length})
                </Button>
                <Button
                  variant="outline"
                  onClick={exportOrderwiseCsv}
                  disabled={!report || (totals.order_count || 0) === 0 || exporting}
                  data-testid="gst-export-orderwise"
                  className="gap-2"
                >
                  <Download className={`w-4 h-4 ${exporting ? 'animate-pulse' : ''}`} />
                  {exporting ? 'Exporting…' : `Order-wise CSV (${totals.order_count || 0})`}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Vendor-wise breakup */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Vendor-wise Breakup</CardTitle>
              <CardDescription className="text-xs">
                Sorted by gross GMV. Vendor names resolved from <code className="text-xs">db.users</code>.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {vendorBreakup.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center" data-testid="gst-vendor-empty">
                  No vendor activity in this period.
                </div>
              ) : (
                <Table data-testid="gst-vendor-breakup-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Vendor</TableHead>
                      <TableHead className="text-right">Orders</TableHead>
                      <TableHead className="text-right">Taxable</TableHead>
                      <TableHead className="text-right">Gross</TableHead>
                      <TableHead className="text-right">CGST</TableHead>
                      <TableHead className="text-right">SGST</TableHead>
                      <TableHead className="text-right">IGST</TableHead>
                      <TableHead className="text-right">Total GST</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vendorBreakup.map((v) => (
                      <TableRow key={v.vendor_id} data-testid={`gst-vendor-row-${v.vendor_id}`}>
                        <TableCell>
                          <div className="font-medium">{v.vendor_name}</div>
                          <div className="text-xs text-slate-400 font-mono">{v.vendor_id?.slice(0, 8)}…</div>
                        </TableCell>
                        <TableCell className="text-right font-mono">{v.order_count}</TableCell>
                        <TableCell className="text-right font-mono text-slate-500">{v.taxable_order_count}</TableCell>
                        <TableCell className="text-right font-mono">{formatINR(v.gross_inr)}</TableCell>
                        <TableCell className="text-right font-mono">{formatINR(v.cgst_inr)}</TableCell>
                        <TableCell className="text-right font-mono">{formatINR(v.sgst_inr)}</TableCell>
                        <TableCell className="text-right font-mono">{formatINR(v.igst_inr)}</TableCell>
                        <TableCell className="text-right font-mono font-medium">{formatINR(v.gst_total_inr)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Source attribution */}
          <div className="text-xs text-slate-400 text-right" data-testid="gst-source-line">
            Source: {report.source} · tax_ledger_used: {String(report.tax_ledger_used)} · marketplace_revenue_used: {String(report.marketplace_revenue_used)} · Computed at {new Date(report.computed_at).toLocaleString('en-IN')}
          </div>
        </>
      )}
    </div>
  );
}
